import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import LocationsMapPanel from 'screens/locations/components/Locations/LocationsMapPanel'
import LocationActionSheet from 'screens/locations/components/Locations/LocationActionSheet'
import { getCenterTenants } from 'slices/locationSlice'

/**
 * ProjectMap — the project's Map tab, built on the shared LocationsMapPanel so
 * it reads the same as the Locations and Evaluations map surfaces: a vertically
 * scrolling column of cards on the left, the MapLibre surface on the right with
 * lettered markers, the legend, and the Layers panel.
 *
 * The card list is the project's own location first, then the center's tenants
 * when that location is a retail center. The Layers panel's per-radius ring
 * toggles replace the old fixed 1 / 3 / 5 / all-rings action list — same rings,
 * but each one is now independently switchable.
 */
const ProjectMap = () => {
	const dispatch = useDispatch()
	const { id } = useParams()

	const [sheetLocation, setSheetLocation] = useState(null)
	const [sheetOpen, setSheetOpen] = useState(false)

	const { project, loading } = useSelector(state => {
		return {
			loading: state.projects.isLoading,
			project: state.projects.entities[id],
		}
	})

	const locationId = project ? project.projectAsset?._id : null

	const { location } = useSelector(state => {
		return {
			location: state.locations.entities[locationId],
		}
	})

	// Optional chaining: this runs before the `!location` guard further down, so
	// a project whose location has not loaded yet would otherwise throw here and
	// blank the tab rather than reaching that guard.
	const isCenter = location?.assetType === 'center'

	const { centerTenantEntities, centerTenantIds, centerTenantsInitialized } = useSelector(
		state => state.locations,
	)

	useEffect(() => {
		if (!centerTenantsInitialized && isCenter) {
			dispatch(getCenterTenants(locationId))
		}
	}, [dispatch, centerTenantsInitialized, locationId, isCenter])

	// The center itself leads the list, so it takes marker "A"; its tenants
	// follow in the order the slice returned them. Deduped in case the center
	// also comes back as one of its own tenants.
	const mapLocations = useMemo(() => {
		const tenants = (centerTenantIds || []).map(tid => centerTenantEntities[tid]).filter(Boolean)
		const out = []
		const seen = new Set()
		for (const loc of [location, ...tenants]) {
			if (!loc?._id) continue
			const key = String(loc._id)
			if (seen.has(key)) continue
			seen.add(key)
			out.push(loc)
		}
		return out
	}, [location, centerTenantIds, centerTenantEntities])

	// A card or marker click opens the location's edit slider in place. Saving
	// dispatches patchLocation, which refreshes the entity this screen already
	// reads, so the card, legend and pin update without a reload.
	const handleSelectLocation = useCallback(selected => {
		if (!selected?._id) return
		setSheetLocation(selected)
		setSheetOpen(true)
	}, [])

	// TODO: Create skeleton loader
	if (loading || !location) return null

	// A location with no coordinates cannot be mapped. Returning null here left
	// the whole tab blank with no explanation, which is indistinguishable from
	// a broken screen (DT-037). Say what is missing and how to fix it instead.
	if (!location.latitude || !location.longitude) {
		return (
			<div className="grid grid-cols-12 gap-6">
				<div className="col-span-12">
					<div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
						<h2 className="text-[22px] font-normal text-gray-700 m-0">No map available yet</h2>
						<p className="text-sm text-gray-500 mt-2 mb-0">
							{location.assetName ? `"${location.assetName}"` : 'This location'} has no
							coordinates, so it cannot be placed on a map. Add a street address on the
							location's Edit tab and it will appear here.
						</p>
					</div>
				</div>
			</div>
		)
	}

	return (
		<>
			<LocationsMapPanel
				locations={mapLocations}
				onSelectLocation={handleSelectLocation}
				legendTitle={isCenter ? 'Center & tenants' : 'Location'}
				showOtherRetail
				/* The project's map subject is its `projectAsset`, which is a
				   Location. So this screen and the location's own Map tab share
				   one saved retailer list, which is intended: what is trading
				   nearby is a property of the place, not of the screen. */
				anchorType="Location"
				anchorId={locationId}
			/>

			{/* Edit only. Archive and Delete are deliberately hidden here — this
			    location is the project's own asset, and removing it from the
			    project's map tab would leave the project without one. */}
			<LocationActionSheet
				location={sheetLocation}
				open={sheetOpen}
				onClose={() => setSheetOpen(false)}
				showActions={false}
			/>
		</>
	)
}

export default ProjectMap
